// Header.tsx
import logoHeader from '@/assets/images/header/logo_header.svg'
import { Button } from '@/components/atoms/Button/Button'
import { LanguageSelector } from '@/components/molecules/LanguageSelector/LanguageSelector'
import { Logo } from '@/components/molecules/Logo/Logo'
import { useModal } from '@/hooks/useModal'
import { motion } from 'framer-motion'
import { Menu } from 'lucide-react'
import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import { MenuMobile } from '../MenuMobile/MenuMobile'
import { Modal } from '../Modal/Modal'

interface HeaderProps {
  isError?: boolean
}

const Header: React.FC<HeaderProps> = ({ isError = false }) => {
  const { t } = useTranslation()
  const [isOpenModal, openModal, closeModal] = useModal(false)
  const navigate = useNavigate()
  const location = useLocation()

  // Scroll to top cuando cambia la ruta
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  const handleScroll = (sectionId: string): void => {
    if (isError) {
      navigate('/')
    } else {
      const section = document.getElementById(sectionId)
      if (section != null) {
        section.scrollIntoView({ behavior: 'smooth' })
      }
    }
  }

  return (
    <>
      {/* Header para Desktop */}
      <motion.header
        className='hidden lg:flex fixed top-12 left-12 z-50 bg-white/90 shadow-md rounded-lg w-[214px]'
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className='flex flex-col justify-center items-center w-full gap-6'>
          <div className='bg-primary p-2 rounded-t-lg w-full flex items-center justify-center'>
            <div className='rounded-t-lg w-[141px]'>
              <Logo src={logoHeader} alt='synapses Hong Kong Logo' width={101} height={80} />
            </div>
          </div>

          <nav className='w-full'>
            <ul className='flex flex-col items-center gap-2 w-full'>
              <li className='w-full text-center'>
                <button
                  onClick={() => navigate('/')}
                  className='w-full text-primary hover:text-[#4DEEDB] font-normal'
                >
                  {t('header.home')}
                </button>
              </li>
              <li className='w-full text-center'>
                <button
                  onClick={() => navigate('/about')}
                  className='w-full text-primary hover:text-[#4DEEDB] font-normal'
                >
                  {t('header.about')}
                </button>
              </li>
              <li className='w-full text-center'>
                <button
                  onClick={() => navigate('/services')}
                  className='w-full text-primary hover:text-[#4DEEDB] font-normal'
                >
                  {t('header.services')}
                </button>
              </li>
            </ul>
          </nav>

          <div className='flex flex-col items-center gap-4 w-full px-6 pb-4'>
            <Button className='w-full text-[#4DEEDB]' size='sm' onClick={() => handleScroll('formContactSynapses')}>
              {t('header.contact')}
            </Button>
            <LanguageSelector className='w-full justify-center' basic />
          </div>
        </div>
      </motion.header>

      {/* Header para Mobile */}
      <motion.header
        className='lg:hidden fixed top-0 left-0 right-0 z-50 p-4 flex justify-end items-center mb-4'
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <button
          onClick={openModal}
          className='p-2 rounded-lg hover:bg-gray-100 transition-colors'
          aria-label='Open menu'
        >
          <Menu size={24} className='text-[#007EA1]' />
        </button>
      </motion.header>

      <Modal isOpenModal={isOpenModal} closeModal={closeModal}>
        <MenuMobile closeModal={closeModal} />
      </Modal>
    </>
  )
}

export default Header
